/**
 * Enhanced Date Utilities for Recurring Transactions
 *
 * Provides comprehensive date calculation functions for all recurrence patterns,
 * handling edge cases like month ends, leap years, and timezone considerations.
 */

import {
  addDays,
  addWeeks,
  addMonths,
  addYears,
  endOfMonth,
  isLastDayOfMonth,
  format,
  parseISO,
  isValid,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { RecurrencePattern, RecurrenceConfig } from '@/types/transaction';

// Pattern translations for Portuguese
export const RECURRENCE_PATTERNS = {
  daily: { label: 'Diariamente', short: 'Diário' },
  weekly: { label: 'Semanalmente', short: 'Semanal' },
  monthly: { label: 'Mensalmente', short: 'Mensal' },
  yearly: { label: 'Anualmente', short: 'Anual' },
} as const;

/**
 * Calculate the next occurrence date based on pattern and interval
 * Handles edge cases like month ends and leap years
 */
export function calculateNextRecurrenceDate(
  currentDate: Date,
  pattern: RecurrencePattern,
  interval: number = 1
): Date {
  if (!isValid(currentDate) || interval <= 0) {
    throw new Error('Invalid date or interval');
  }

  let nextDate: Date;

  switch (pattern) {
    case 'daily':
      nextDate = addDays(currentDate, interval);
      break;

    case 'weekly':
      nextDate = addWeeks(currentDate, interval);
      break;

    case 'monthly':
      // Handle month end edge case
      if (isLastDayOfMonth(currentDate)) {
        nextDate = endOfMonth(addMonths(currentDate, interval));
      } else {
        nextDate = addMonths(currentDate, interval);
      }
      break;

    case 'yearly':
      // Handle leap year edge case (Feb 29)
      if (currentDate.getMonth() === 1 && currentDate.getDate() === 29) {
        nextDate = addYears(currentDate, interval);
        // If not a leap year, use Feb 28
        if (!isLeapYear(nextDate.getFullYear())) {
          nextDate.setDate(28);
        }
      } else {
        nextDate = addYears(currentDate, interval);
      }
      break;

    default:
      throw new Error(`Invalid recurrence pattern: ${pattern}`);
  }

  return nextDate;
}

/**
 * Generate all occurrence dates for a recurrence pattern
 */
export function generateRecurrenceDates(
  startDate: Date,
  config: RecurrenceConfig,
  maxDates: number = 100 // Safety limit
): Date[] {
  if (!isValid(startDate)) {
    throw new Error('Invalid start date');
  }

  const dates: Date[] = [];
  let currentDate = new Date(startDate);
  let count = 0;

  while (count < maxDates) {
    // Check count limit
    if (config.count && count >= config.count) {
      break;
    }

    // Check end date limit
    if (config.end_date && currentDate > new Date(config.end_date)) {
      break;
    }

    dates.push(new Date(currentDate));
    currentDate = calculateNextRecurrenceDate(currentDate, config.pattern, config.interval);
    count++;
  }

  return dates;
}

/**
 * Generate a preview of upcoming dates (limited to 10 for UI)
 */
export function generateRecurrencePreview(
  startDate: Date,
  config: RecurrenceConfig
): { dates: Date[]; hasMore: boolean } {
  const allDates = generateRecurrenceDates(startDate, config, 10);
  return {
    dates: allDates,
    hasMore: config.count ? config.count > 10 : !!config.end_date,
  };
}

/**
 * Calculate the total number of occurrences between two dates
 */
export function calculateRecurrenceCount(
  startDate: Date,
  endDate: Date,
  pattern: RecurrencePattern,
  interval: number = 1
): number {
  if (!isValid(startDate) || !isValid(endDate) || startDate >= endDate) {
    return 0;
  }

  let count = 0;
  let currentDate = new Date(startDate);
  const safetyLimit = 10000; // Prevent infinite loops

  while (currentDate <= endDate && count < safetyLimit) {
    count++;
    currentDate = calculateNextRecurrenceDate(currentDate, pattern, interval);
  }

  return count;
}

/**
 * Get a human-readable description of the recurrence pattern
 */
export function generateRecurrenceDescription(
  pattern: RecurrencePattern,
  interval: number = 1,
  count?: number,
  endDate?: Date | string | null
): string {
  const patternLabel = RECURRENCE_PATTERNS[pattern].label.toLowerCase();

  let description = '';

  // Handle interval
  if (interval === 1) {
    description = patternLabel;
  } else {
    // Get ordinal suffix for Portuguese
    const ordinal = getOrdinalSuffix(interval);
    description = `a cada ${ordinal} ${patternLabel}`;
  }

  // Add count limitation
  if (count) {
    description += `, ${count} vez${count > 1 ? 'es' : ''}`;
  }

  // Add end date
  if (endDate) {
    const date = typeof endDate === 'string' ? parseISO(endDate) : endDate;
    if (isValid(date)) {
      description += `, até ${format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}`;
    }
  }

  return description.charAt(0).toUpperCase() + description.slice(1);
}

/**
 * Get Portuguese ordinal suffix
 */
function getOrdinalSuffix(num: number): string {
  if (num === 1) return '1ª';
  if (num === 2) return '2ª';
  if (num === 3) return '3ª';
  if (num === 4) return '4ª';
  if (num === 5) return '5ª';
  if (num === 6) return '6ª';
  if (num === 7) return '7ª';
  if (num === 8) return '8ª';
  if (num === 9) return '9ª';
  if (num === 10) return '10ª';

  // For numbers > 10, just return the number
  return num.toString();
}

/**
 * Check if a year is a leap year
 */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Validate recurrence configuration
 */
export function validateRecurrenceConfig(config: RecurrenceConfig): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Validate pattern
  if (!Object.values(RECURRENCE_PATTERNS).some(p => p.label.includes(config.pattern))) {
    errors.push('Padrão de recorrência inválido');
  }

  // Validate interval
  if (config.interval <= 0 || config.interval > 999) {
    errors.push('Intervalo deve estar entre 1 e 999');
  }

  // Validate count
  if (config.count !== undefined && config.count <= 0) {
    errors.push('Número de ocorrências deve ser maior que 0');
  }

  // Validate end date
  if (config.end_date) {
    const endDate = typeof config.end_date === 'string' ? parseISO(config.end_date) : config.end_date;
    if (!isValid(endDate) || endDate <= new Date()) {
      errors.push('Data de término deve ser uma data válida no futuro');
    }
  }

  // Validate that at least one limit is set
  if (!config.count && !config.end_date) {
    errors.push('Defina um número de ocorrências ou uma data de término');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Get the next date for all active recurring transactions
 * This would typically be called by a scheduled job
 */
export async function getNextOccurrencesForAllActive(
  fetchTransactions: () => Promise<any[]>
): Promise<{ transactionId: string; nextDate: Date }[]> {
  const transactions = await fetchTransactions();
  const results: { transactionId: string; nextDate: Date }[] = [];

  for (const transaction of transactions) {
    if (transaction.is_recurring) {
      // Check if more occurrences should be created
      const shouldCreate = await shouldCreateMoreOccurrences(transaction);

      if (shouldCreate) {
        const nextDate = calculateNextRecurrenceDate(
          new Date(transaction.date),
          transaction.recurrence_pattern,
          transaction.recurrence_interval
        );

        results.push({
          transactionId: transaction.id,
          nextDate,
        });
      }
    }
  }

  return results;
}

/**
 * Check if a recurring transaction should generate more occurrences
 */
async function shouldCreateMoreOccurrences(transaction: any): Promise<boolean> {
  // Check count limit
  if (transaction.recurrence_count &&
      transaction.recurrence_created_count >= transaction.recurrence_count) {
    return false;
  }

  // Check end date limit
  if (transaction.recurrence_end_date) {
    const endDate = new Date(transaction.recurrence_end_date);
    const lastChildDate = await getLastChildDate(transaction.id);
    const nextDate = calculateNextRecurrenceDate(
      lastChildDate || new Date(transaction.date),
      transaction.recurrence_pattern,
      transaction.recurrence_interval
    );

    if (nextDate > endDate) {
      return false;
    }
  }

  return true;
}

/**
 * Get the date of the last child transaction
 */
async function getLastChildDate(parentTransactionId: string): Promise<Date | null> {
  // This would be implemented with your data access layer
  // For now, return null as a placeholder
  return null;
}

/**
 * Calculate statistics for recurring transactions
 */
export function calculateRecurrenceStats(
  transactions: any[]
): {
  totalActive: number;
  totalCompleted: number;
  totalPaused: number;
  monthlyValue: number;
  nextTransactionDate: Date | null;
} {
  const now = new Date();
  let totalActive = 0;
  let totalCompleted = 0;
  let totalPaused = 0;
  let monthlyValue = 0;
  let nextTransactionDate: Date | null = null;

  for (const transaction of transactions) {
    if (!transaction.is_recurring) continue;

    // Determine status
    const isActive = !transaction.recurrence_end_date ||
                    new Date(transaction.recurrence_end_date) >= now;

    if (isActive && !transaction.recurrence_count) {
      totalActive++;
    } else if (isActive && transaction.recurrence_count >
               transaction.recurrence_created_count) {
      totalActive++;
    } else if (transaction.recurrence_count &&
               transaction.recurrence_created_count >= transaction.recurrence_count) {
      totalCompleted++;
    } else {
      totalPaused++;
    }

    // Calculate monthly value
    if (transaction.recurrence_pattern === 'monthly') {
      monthlyValue += transaction.amount;
    }

    // Find next transaction date
    if (isActive) {
      const nextDate = calculateNextRecurrenceDate(
        new Date(transaction.date),
        transaction.recurrence_pattern,
        transaction.recurrence_interval
      );

      if (!nextTransactionDate || nextDate < nextTransactionDate) {
        nextTransactionDate = nextDate;
      }
    }
  }

  return {
    totalActive,
    totalCompleted,
    totalPaused,
    monthlyValue,
    nextTransactionDate,
  };
}

/**
 * Format date for display in Portuguese
 */
export function formatDateForDisplay(date: Date | string, formatStr: string = 'dd/MM/yyyy'): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, formatStr, { locale: ptBR });
}

/**
 * Parse date string from various formats
 */
export function parseDateString(dateStr: string): Date | null {
  if (!dateStr) return null;

  // Try ISO format first
  let date = parseISO(dateStr);
  if (isValid(date)) return date;

  // Try Brazilian format (dd/MM/yyyy)
  const brazilianParts = dateStr.split('/');
  if (brazilianParts.length === 3) {
    date = new Date(
      parseInt(brazilianParts[2]), // year
      parseInt(brazilianParts[1]) - 1, // month (0-indexed)
      parseInt(brazilianParts[0]) // day
    );
    if (isValid(date)) return date;
  }

  return null;
}

/**
 * Check if a date is in the past
 */
export function isDateInPast(date: Date | string): boolean {
  const d = typeof date === 'string' ? parseISO(date) : date;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
}

/**
 * Check if a recurrence pattern is valid
 */
export function isValidRecurrencePattern(pattern: string): pattern is RecurrencePattern {
  return ['daily', 'weekly', 'monthly', 'yearly'].includes(pattern);
}